import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type MessageSeverity =
  | 'success'
  | 'info'
  | 'warn'
  | 'error'
  | 'secondary'
  | 'contrast';

/**
 * An inline status banner.
 *
 * No ng-primitives equivalent — its `toast` primitive covers transient,
 * stacked notifications, which is a different thing from a banner that
 * sits in the layout.
 *
 * The roles are load-bearing and stay even while unstyled: messages
 * appear in response to something the user just did, so `alert` for
 * errors (interrupts) and `status` for everything else (waits for a
 * pause). Both are live regions, so a message appearing after load is
 * announced without moving focus.
 */
@Component({
  selector: 'hh-message',
  standalone: true,
  template: `
    <div [class]="classes()" [attr.role]="role()">
      <ng-content />
    </div>
  `,
  styleUrl: './hh-message.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhMessageComponent {
  readonly severity = input<MessageSeverity>('info');

  protected readonly classes = computed(() => `hh-message hh-message--${this.severity()}`);

  /** Errors interrupt; everything else waits for a natural pause. */
  protected readonly role = computed(() => (this.severity() === 'error' ? 'alert' : 'status'));
}
